import { useState } from 'react'
import { App, Alert, Button, Drawer, Empty, Form, Input, List, Space, Tag, Typography } from 'antd'
import { MessageOutlined } from '@ant-design/icons'

import {
  useAskSupport,
  useReplyToSupport,
  useSupportDesk,
  useSupportThread,
} from '../api/hooks'
import type { SupportConversation } from '../api/types'

/**
 * Asking a person.
 *
 * The response target is shown before anybody types, not after they send: a
 * promise discovered afterwards is not a promise anybody relied on. Where the
 * plan states no target it says so — "we answer, we do not promise when" — which
 * is less impressive and is the truth.
 *
 * Answers arrive from somebody replying on their phone, so an open thread polls
 * rather than waiting to be reloaded, and the reply is emailed as well: the
 * person who asked has usually closed the tab, and a thread they never return
 * to looks exactly like being ignored.
 */

const SUPPORT_EMAIL = 'peccancypeople@gmail.com'

function targetSentence(days: number): string {
  if (days <= 0) {
    return 'We answer every message. Your plan does not include a stated response time.'
  }
  if (days === 1) {
    return 'Your plan is answered within 1 working day.'
  }
  return `Your plan is answered within ${days} working days.`
}

export function SupportWidget() {
  const [open, setOpen] = useState(false)
  const [thread, setThread] = useState<string | null>(null)

  const desk = useSupportDesk(open)

  return (
    <>
      <Button
        type="primary"
        shape="round"
        icon={<MessageOutlined />}
        onClick={() => setOpen(true)}
        style={{ position: 'fixed', right: 24, bottom: 24, zIndex: 900, boxShadow: '0 6px 20px rgba(0,0,0,0.25)' }}
      >
        Help
      </Button>

      <Drawer
        title={thread ? 'Conversation' : 'Talk to us'}
        open={open}
        width={460}
        onClose={() => {
          setOpen(false)
          setThread(null)
        }}
      >
        {thread ? (
          <Thread id={thread} onBack={() => setThread(null)} />
        ) : (
          <Desk
            conversations={desk.data?.conversations ?? []}
            responseDays={desk.data?.response_days ?? 0}
            available={desk.data?.available ?? true}
            loading={desk.isLoading}
            onOpen={setThread}
          />
        )}
      </Drawer>
    </>
  )
}

function Desk({
  conversations,
  responseDays,
  available,
  loading,
  onOpen,
}: {
  conversations: SupportConversation[]
  responseDays: number
  available: boolean
  loading: boolean
  onOpen: (id: string) => void
}) {
  const { message } = App.useApp()
  const [body, setBody] = useState('')
  const ask = useAskSupport()

  async function send() {
    try {
      const conversation = await ask.mutateAsync({ body: body.trim() })
      setBody('')
      onOpen(conversation.id)
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Could not send that')
    }
  }

  return (
    <Space direction="vertical" size={20} style={{ width: '100%' }}>
      {!available ? (
        <Alert
          type="warning"
          showIcon
          message="The direct channel is not connected right now"
          description={
            <>
              Your message is still kept and will be answered, but it may take longer. If it is
              urgent, write to <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
            </>
          }
        />
      ) : null}

      <div>
        <Typography.Text type="secondary">{targetSentence(responseDays)}</Typography.Text>
      </div>

      <Form layout="vertical" onFinish={send}>
        <Form.Item label="What is happening?" style={{ marginBottom: 12 }}>
          <Input.TextArea
            rows={5}
            maxLength={4000}
            showCount
            value={body}
            placeholder="What you expected, what happened, and the service or profile it happened on."
            onChange={(event) => setBody(event.target.value)}
          />
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={ask.isPending} disabled={!body.trim()}>
          Send
        </Button>
      </Form>

      <div>
        <Typography.Text strong>Earlier</Typography.Text>
        <List
          loading={loading}
          dataSource={conversations}
          locale={{ emptyText: <Empty description="Nothing yet." image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
          renderItem={(conversation) => (
            <List.Item
              style={{ cursor: 'pointer' }}
              onClick={() => onOpen(conversation.id)}
              actions={[
                <Tag key="status" color={conversation.status === 'answered' ? 'green' : undefined}>
                  {conversation.status === 'open' ? 'with us' : conversation.status}
                </Tag>,
              ]}
            >
              <List.Item.Meta
                title={conversation.subject}
                description={new Date(conversation.updated_at).toLocaleString()}
              />
            </List.Item>
          )}
        />
      </div>
    </Space>
  )
}

function Thread({ id, onBack }: { id: string; onBack: () => void }) {
  const { message } = App.useApp()
  const thread = useSupportThread(id)
  const reply = useReplyToSupport(id)
  const [body, setBody] = useState('')

  async function send() {
    try {
      await reply.mutateAsync(body.trim())
      setBody('')
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Could not send that')
    }
  }

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Button size="small" onClick={onBack}>
        ← All conversations
      </Button>

      <Typography.Title level={5} style={{ marginBottom: 0 }}>
        {thread.data?.subject}
      </Typography.Title>

      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        {(thread.data?.messages ?? []).map((entry) => (
          <div
            key={entry.id}
            style={{
              alignSelf: entry.author === 'support' ? 'flex-start' : 'flex-end',
              background: entry.author === 'support' ? '#f0f5ff' : '#f6f6f6',
              border: '1px solid rgba(0,0,0,0.06)',
              borderRadius: 10,
              padding: '10px 14px',
              maxWidth: '92%',
            }}
          >
            <Typography.Text style={{ whiteSpace: 'pre-wrap' }}>{entry.body}</Typography.Text>
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                {entry.author === 'support' ? entry.author_name || 'PecGoProf' : 'You'} ·{' '}
                {new Date(entry.created_at).toLocaleString()}
              </Typography.Text>
            </div>
          </div>
        ))}
      </Space>

      <Form onFinish={send}>
        <Input.TextArea
          rows={3}
          maxLength={4000}
          value={body}
          placeholder="Add something"
          onChange={(event) => setBody(event.target.value)}
        />
        <Button
          type="primary"
          htmlType="submit"
          loading={reply.isPending}
          disabled={!body.trim()}
          style={{ marginTop: 10 }}
        >
          Send
        </Button>
      </Form>
    </Space>
  )
}
